// store/forms/delForm/fieldNotes/actions.js

import { FieldNoteActivity } from "../../../../models/Del/FieldNotes";

export const makeFieldNotesActions = ({ getDraft, bump, setFieldNotesUi }) => ({
  collapseGeneralWorkDescriptionWeather(bool) {
    setFieldNotesUi({ generalWorkDescriptionWeatherCollapsed: bool });
    bump();
  },

  updateGeneralWorkDescription(value, sendDataToBackend = false, emitPatch) {
    const draft = getDraft();
    draft.fieldNotes.updateWorkDescription(value);

    if (sendDataToBackend) {
      const patch = draft.fieldNotes.toPatch("update", {
        workDescription: draft.fieldNotes.workDescription,
      });
      emitPatch(patch);

      // Only update JSA job description if it's currently empty
      // Check happens here (on blur) to avoid timing issues with onChange calls
      if (
        !draft.jsa.jsaBasicInfo.jsaJobDescription ||
        draft.jsa.jsaBasicInfo.jsaJobDescription.length === 0
      ) {
        draft.jsa.jsaBasicInfo.setJsaJobDescription(value);
        const jsaPatch = {
          patch_type: "jsa",
          jsa_data: draft.jsa.jsaBasicInfo.toPatch({
            jsaJobDescription: value,
          }),
        };
        emitPatch(jsaPatch);
      }
    }
    bump();
  },

  updateWeather(value, sendDataToBackend = false, emitPatch) {
    const draft = getDraft();
    draft.fieldNotes.updateWeather(value);
    if (sendDataToBackend) {
      const patch = draft.fieldNotes.toPatch("update", {
        weather: draft.fieldNotes.weather,
      });
      emitPatch(patch);
    }
    bump();
  },

  removeActivity(activityRowId, emitPatch) {
    const draft = getDraft();
    draft.fieldNotes.removeActivity(activityRowId);
    const patch = {
      patch_type: "field_notes_activity",
      action: "remove",
      id: activityRowId,
    };
    emitPatch(patch);
    bump();
  },

  resetNoteOrder(newActivities, emitPatch) {
    const draft = getDraft();
    draft.fieldNotes.resetNoteOrder(newActivities);
    const patch = {
      patch_type: "field_notes_reorder",
      items: newActivities.map((a, i) => ({ id: a.rowId, note_order: i })),
    };
    emitPatch(patch);
    bump();
  },

  // Save activity - handles both new and edit cases
  // Called from the local modal state in fieldNotesComponents.js
  saveActivity(activityData, emitPatch) {
    const draft = getDraft();
    const { isEditing, activityRowId, time, notes } = activityData;

    if (isEditing && activityRowId) {
      // Editing existing activity
      const activityItem = draft.fieldNotes.activities.find(
        (a) => a.rowId === activityRowId
      );
      if (activityItem) {
        activityItem.updateTime(time);
        activityItem.updateNotes(notes);
        const patch = activityItem.toPatch("update", {
          time: time,
          notes: notes,
          noteOrder: activityItem.noteOrder,
        });
        emitPatch(patch);
      }
    } else {
      // Adding new activity
      draft.fieldNotes.addBlankNewActivity();
      const newActivity =
        draft.fieldNotes.activities[draft.fieldNotes.activities.length - 1];
      newActivity.updateTime(time);
      newActivity.updateNotes(notes);
      const patch = newActivity.toPatch("add", {
        time: time,
        notes: notes,
        noteOrder: newActivity.noteOrder,
      });
      emitPatch(patch);
    }
    bump();
  },

  handleBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft || !draft.fieldNotes) return;
    // field_notes is a field update on the single FieldNotes object
    applyPatchToObject(draft.fieldNotes, patch);
    bump();
  },

  handleReorderBroadcast(patch) {
    const draft = getDraft();
    if (!draft || !draft.fieldNotes) return;

    const { items } = patch;
    if (!items || !items.length) return;

    // Update each activity's noteOrder from the broadcast
    for (const item of items) {
      const activity = draft.fieldNotes.activities.find(
        (a) => a.rowId === item.id
      );
      if (activity) {
        activity.noteOrder = item.note_order;
      }
    }

    // Sort activities by new note_order
    draft.fieldNotes.activities.sort((a, b) => a.noteOrder - b.noteOrder);
    bump();
  },

  handleActivityBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft || !draft.fieldNotes) return;

    const { id, action } = patch;

    if (action === "add") {
      const existing = draft.fieldNotes.activities.find(
        (a) => a.rowId === id
      );
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newActivity = new FieldNoteActivity(patch);
        draft.fieldNotes.activities.push(newActivity);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = draft.fieldNotes.activities.findIndex(
        (a) => a.rowId === id
      );
      if (idx >= 0) {
        draft.fieldNotes.activities.splice(idx, 1);
        // Re-index note orders after removal
        draft.fieldNotes.activities.forEach((a, i) => {
          a.noteOrder = i;
        });
        bump();
      }
      return;
    }

    // Update existing activity
    const activity = draft.fieldNotes.activities.find(
      (a) => a.rowId === id
    );
    if (activity) {
      applyPatchToObject(activity, patch);
      bump();
    } else {
      // Fallback: create if not found
      const newActivity = new FieldNoteActivity(patch);
      draft.fieldNotes.activities.push(newActivity);
      bump();
    }
  },
});
